import "./DatesSection.css";

import { EditGreyIcon } from "../../../../../../../assets";
import { ProjectDatesConstants } from "../../../../../../../constants";
import { Column, Divider, ReadonlyText, Row } from "../../../../../../../widget";
import {
  buildClassifiedDateFields,
  buildSecondAssessmentDateField,
  buildUnclassifiedDateFields,
  ClassifiedDateFieldDescriptor,
  classifiedKeysOf,
  isDateSchemaEmpty,
  NOT_AVAILABLE,
  ReadonlyDateField,
} from "./buildUnclassifiedDateFields";
import { useDatesSection } from "./useDatesSection";

interface DatesSectionProps {
  handleEditDatesClick: () => void;
}

/** Dates-group fields this panel renders explicitly, in display order (with its own labels). */
const DATE_FIELD_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "design.registrationDate",
    label: "Registration date",
    tooltipText: ProjectDatesConstants.DESIGN_REGISTRATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.implementationDate",
    label: "Project implementation date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_IMPLEMENTATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.certificationDate",
    label: "Certification date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_CERTIFICATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.startDate",
    label: "Project start date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_START_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "implementation.restorationValidationDate",
    label: "Restoration validation date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_RESTORATION_VALIDATION_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "duration.duration",
    label: "Project duration",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_DURATION_TOOLTIP_TEXT,
  },
];

/** Monitoring-group fields rendered before the derived second-assessment date. */
const PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "implementation.validationDate",
    label: "Validation date",
    tooltipText: ProjectDatesConstants.IMPLEMENTATION_FIRST_ASSESSMENT_DATE_TOOLTIP_TEXT,
  },
];

/** Monitoring-group fields rendered after the derived second-assessment date. */
const POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS: ClassifiedDateFieldDescriptor[] = [
  {
    schemaKey: "ongoingMonitoring.finalAssessmentDate",
    label: "Final assessment date",
    tooltipText: ProjectDatesConstants.ONGOING_MONITORING_FINAL_ASSESSMENT_DATE_TOOLTIP_TEXT,
  },
  {
    schemaKey: "ongoingMonitoring.subsequentFrequency",
    label: "Monitoring frequency",
    tooltipText: ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_TOOLTIP_TEXT,
  },
];

/** Derived from the descriptors so it never drifts from the fields actually rendered explicitly. */
const CLASSIFIED_DATE_KEYS = classifiedKeysOf(
  DATE_FIELD_DESCRIPTORS,
  PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS,
  POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS
);

const renderFieldRows = (fields: ReadonlyDateField[]): JSX.Element[] => {
  const rowCount = Math.ceil(fields.length / 2);

  return Array.from({ length: rowCount }, (_unused, rowIndex) => {
    const leftField = fields[rowIndex * 2];
    const rightField = fields[rowIndex * 2 + 1];

    return (
      <Row spacingV="ll" key={leftField.key}>
        <Column span={4}>
          <ReadonlyText
            label={leftField.label}
            value={leftField.value ?? NOT_AVAILABLE}
            noDataAvailable={!leftField.value}
            tooltipText={leftField.tooltipText}
            tooltipHeader=""
          />
        </Column>
        {rightField && (
          <Column span={4} offset={1}>
            <ReadonlyText
              label={rightField.label}
              value={rightField.value ?? NOT_AVAILABLE}
              noDataAvailable={!rightField.value}
              tooltipText={rightField.tooltipText}
              tooltipHeader=""
            />
          </Column>
        )}
      </Row>
    );
  });
};

/**
 * Read-only project dates shown on the overview — the counterpart to the editable DateSchemaFields.
 * It reads the same date schema (see useDatesSection) and only renders a field when the schema
 * declares it, so the overview matches whatever the edit panel offered.
 *
 * Rendering is two-pass: known fields are pushed explicitly below (fixed label/order/grouping into the
 * Dates vs Monitoring sections), then buildUnclassifiedDateFields appends any remaining schema field
 * not in CLASSIFIED_DATE_KEYS — so a newly-added schema date still appears here without a code change.
 *
 * The whole panel is hidden when the project has no date schema (see isDateSchemaEmpty), rather than
 * rendering empty headings.
 */
export const DatesSection = ({ handleEditDatesClick }: DatesSectionProps): JSX.Element | null => {
  const { dates, isReadOnly, dateSchema } = useDatesSection();

  if (isDateSchemaEmpty(dateSchema)) {
    return null;
  }

  // Explicit fields first, then any remaining schema date fields surfaced generically so nothing newly
  // added to the schema is silently dropped.
  const datesFields: ReadonlyDateField[] = [
    ...buildClassifiedDateFields(dateSchema, dates, DATE_FIELD_DESCRIPTORS),
    ...buildUnclassifiedDateFields(dateSchema, dates, CLASSIFIED_DATE_KEYS),
  ];

  // The derived second-assessment date is interleaved between the validation date and the remaining
  // monitoring fields, preserving the previous ordering.
  const monitoringFields: ReadonlyDateField[] = [
    ...buildClassifiedDateFields(dateSchema, dates, PRE_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS),
    ...buildSecondAssessmentDateField(dates),
    ...buildClassifiedDateFields(dateSchema, dates, POST_SECOND_ASSESSMENT_MONITORING_DESCRIPTORS),
  ];

  return (
    <div className="ProjectTabSection">
      <Row spacingV="ll" justify="between">
        <Column span={6}>
          <h2>Dates</h2>
        </Column>
        <Column span={2} className="editButtonColumn">
          {!isReadOnly && (
            <div className="editDatesButton">
              <EditGreyIcon onClick={() => handleEditDatesClick()} />
            </div>
          )}
        </Column>
      </Row>
      {renderFieldRows(datesFields)}
      <Row spacingV="xl">
        <Column span={12}>
          <Divider type="horizontal" />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={6}>
          <h2>Monitoring</h2>
        </Column>
      </Row>
      {renderFieldRows(monitoringFields)}
    </div>
  );
};
